import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { 
  ImageIcon, 
  Sparkles, 
  Video, 
  Users, 
  Film, 
  Package,
  ChevronRight,
  Lock,
  Zap
} from 'lucide-react';
import Footer from './Footer';
import SEOHead from './SEOHead';

const FEATURES = [
  {
    id: 'social-creatives',
    title: 'Social Media Creatives',
    description: 'Create high-impact static creatives for any social media placement',
    icon: ImageIcon,
    gradient: 'from-purple-500 to-pink-500',
    bgLight: 'bg-purple-50',
    borderColor: 'border-purple-200',
    path: '/creator-ai/social-creatives',
    highlights: ['Auto-sized for all platforms', 'Brand-safe layouts', 'One-click generation'],
    image: 'https://images.unsplash.com/photo-1683721003111-070bcc053d8b?w=500&h=300&fit=crop&q=80',
    locked: false,
    credits: '15 credits / creative',
  },
  {
    id: 'bulk-create',
    title: 'Bulk Create (Occasions)',
    description: 'Create many creatives at once for festivals & events',
    icon: Package,
    gradient: 'from-pink-500 to-rose-500',
    bgLight: 'bg-pink-50',
    borderColor: 'border-pink-200',
    path: '/creator-ai/bulk-create',
    highlights: ['Multiple occasions at once', 'Consistent design style', 'ZIP download'],
    image: 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=500&h=300&fit=crop&q=80',
    locked: false,
    credits: '15 credits / creative',
  },
  {
    id: 'text-to-image',
    title: 'Text to Image',
    description: 'Generate stunning images from text descriptions',
    icon: Sparkles,
    gradient: 'from-purple-500 to-pink-500',
    bgLight: 'bg-purple-50',
    borderColor: 'border-purple-200',
    path: '/creator-ai/text-to-image',
    highlights: ['Text prompts', 'Multiple aspect ratios', 'HD quality'],
    image: 'https://images.unsplash.com/photo-1547891654-e66ed7ebb968?w=500&h=300&fit=crop&q=80',
    locked: false,
    credits: '15 credits / image',
  },
  {
    id: 'heygen-hub',
    title: 'AI Video Studio',
    description: 'Create professional AI videos — prompt-to-video, avatar videos & video translation',
    icon: Video,
    gradient: 'from-violet-500 to-purple-600',
    bgLight: 'bg-violet-50',
    borderColor: 'border-violet-200',
    path: '/creator-ai/heygen-hub',
    highlights: ['Video Agent', 'Avatar Videos', 'Video Translate (20+ languages)'],
    image: 'https://images.unsplash.com/photo-1492619375914-88005aa9e8fb?w=500&h=300&fit=crop&q=80',
    locked: false,
    badge: 'NEW ✨',
    credits: '200 credits / video',
  },
];

const ImageGenerator = () => {
  const { user, openAuth } = useContext(AuthContext);
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFeatureClick = (feature) => {
    if (!user) {
      openAuth('login');
      return;
    }
    
    // Check if feature is locked and user is not admin/subadmin
    const isAdmin = user?.email === 'shifracs@gmail.com' || user?.email === 'Advisor@mednerd.in';
    if (feature.locked && !isAdmin) {
      return; // Do nothing - feature is locked for regular users
    }
    
    navigate(feature.path);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEOHead title="Creator AI — Social Media, Avatars & AI Videos" description="Generate social media creatives, AI avatars, reels and videos in minutes using Nerd Nation's Creator AI suite." path="/image-generator" />
      {/* Hero Section */}
      <div className="bg-[#0a0e17] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10 lg:py-12">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-4 sm:mb-6">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              <span className="text-xs sm:text-sm font-bold text-white">CREATOR AI</span>
            </div>
            
            {/* Title */}
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold gradient-text-gold mb-2 sm:mb-4 font-playfair">
              Creator AI
            </h1>
            
            {/* Subtitle */}
            <p className="text-sm sm:text-lg lg:text-xl text-white max-w-2xl mx-auto mb-5 sm:mb-8 px-2 font-outfit">
              Learn to create Social Media content, Avatars, Reels and Videos using Creator AI
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-xs sm:max-w-lg mx-auto">
              <div className="text-center">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold gradient-text-gold">5</p>
                <p className="text-xs sm:text-sm text-white">Features</p>
              </div>
              <div className="text-center border-x border-white/20">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold gradient-text-gold">∞</p>
                <p className="text-xs sm:text-sm text-white">Creatives</p>
              </div>
              <div className="text-center">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold gradient-text-gold">AI</p>
                <p className="text-xs sm:text-sm text-white">Powered</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.id}
                className="group cursor-pointer bg-[#0a0e17] rounded-2xl border border-white/10 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-gold-primary/30"
                onClick={() => handleFeatureClick(feature)}
              >
                {/* Image Section */}
                <div 
                  className="h-44 sm:h-48 w-full bg-gray-100"
                  style={{
                    backgroundImage: `url(${feature.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className={`h-full w-full bg-gradient-to-b from-black/10 via-transparent to-black/30 p-4 flex flex-col justify-between`}>
                    {/* Icon Badge - Top Left */}
                    <div className="flex items-start justify-between">
                      <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-md">
                        <Icon className="h-6 w-6 text-gray-800" />
                      </div>
                      {feature.badge && (
                        <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2.5 py-1 rounded-full shadow">
                          {feature.badge}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-5">
                  <h3 className="font-bold gradient-text-gold text-base sm:text-lg md:text-xl mb-1.5 font-outfit">
                    {feature.title}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-white mb-4 font-outfit">
                    {feature.description}
                  </p>
                  
                  {/* Highlights */}
                  <div className="space-y-1.5 mb-4">
                    {feature.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs text-white font-outfit">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold-primary"></div>
                        {highlight}
                      </div>
                    ))}
                  </div>

                  {/* Credits badge */}
                  {feature.credits && (
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold mb-4 bg-gradient-to-r ${feature.gradient} bg-opacity-10`}
                      style={{background: 'transparent'}}
                    >
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-gradient-to-r ${feature.bgLight} ${feature.borderColor}`}>
                        <Zap className="h-3 w-3 text-yellow-500 fill-yellow-400" />
                        <span className="text-white">{feature.credits}</span>
                      </span>
                    </div>
                  )}
                  
                  {/* CTA Button */}
                  <Button
                    className={`w-full bg-gradient-to-r ${feature.gradient} hover:opacity-90 text-white font-semibold py-3 rounded-xl text-sm ${
                      feature.locked && user && user?.email !== 'shifracs@gmail.com' && user?.email !== 'Advisor@mednerd.in' ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    {(!user || (feature.locked && user?.email !== 'shifracs@gmail.com' && user?.email !== 'Advisor@mednerd.in')) && (
                      <Lock className="h-4 w-4 mr-2" />
                    )}
                    <span>
                      {!user 
                        ? 'Login to Use' 
                        : feature.locked && user?.email !== 'shifracs@gmail.com' && user?.email !== 'Advisor@mednerd.in'
                        ? 'Coming Soon'
                        : 'Open Feature'
                      }
                    </span>
                    {user && (!feature.locked || user?.email === 'shifracs@gmail.com' || user?.email === 'Advisor@mednerd.in') && (
                      <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* How It Works */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="text-center mb-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold gradient-text-gold mb-2 font-playfair">
            How Creator AI Works
          </h2>
          <p className="text-sm sm:text-base text-white font-outfit">Simple 3-step process for any feature</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-gold-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-xl font-bold gradient-text-gold">1</span>
            </div>
            <h3 className="font-semibold text-white mb-1 font-outfit">Choose & Input</h3>
            <p className="text-sm text-white font-outfit">Select platform, add your content and brand</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-gold-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-xl font-bold gradient-text-gold">2</span>
            </div>
            <h3 className="font-semibold text-white mb-1 font-outfit">AI Creates</h3>
            <p className="text-sm text-white font-outfit">Our AI handles design, layout & production</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-gold-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-xl font-bold gradient-text-gold">3</span>
            </div>
            <h3 className="font-semibold text-white mb-1 font-outfit">Download & Post</h3>
            <p className="text-sm text-white font-outfit">Get platform-ready content instantly</p>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="bg-[#0a0e17] mt-8 sm:mt-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="text-center">
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold gradient-text-gold mb-2 sm:mb-4 font-playfair">
              Ready to Create Amazing Content?
            </h2>
            <p className="text-sm sm:text-lg text-white mb-5 sm:mb-8 max-w-2xl mx-auto px-2 font-outfit">
              Start creating professional social media content, reels, and videos in minutes
            </p>
            <Button
              onClick={() => !user ? openAuth('login') : window.scrollTo({ top: 400, behavior: 'smooth' })}
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-lg rounded-lg sm:rounded-xl hover:shadow-lg transition-all"
            >
              {user ? 'Start Creating' : 'Login to Get Started'}
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 ml-1.5 sm:ml-2" />
            </Button>
          </div>
        </div>
      </div>

      <div className="mb-20 sm:mb-24"></div>
      <Footer />
    </div>
  );
};

export default ImageGenerator;
